import { HubConnection, IHttpConnectionOptions } from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { MultiMessageEnum } from '@app/facade-services/multi-message-enum';
import { onSignalRStatus, onSignalVersionDiff } from '@app/store/client-slices/signal-r/signal-r.actions';
import { AppConfigService } from '@app/services/config/app-config.service';
import { patchManyFenceResult, purgeFenceResult } from '@app/store/faxe/fence-result/fence-result.actions';
import { MultiMessageOperationEnum } from '@app/facade-services/multi-message-operation-enum';
import { FenceResult } from '@app/rest-client/faxe/models/fence-result';
import { patchManyRiderEvents, purgeRiderEvents } from '@app/store/faxe/rider-event/rider-event.actions';
import { RiderEvent } from '@app/rest-client/faxe/models/rider-event';
import { Rider } from '@app/rest-client/faxe/models/rider';
import { fetchAllRider, patchManyRider, purgeRider, updateManyRider } from '@app/store/faxe/rider/rider.actions';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { PasswordService } from '@app/services/password/password.service';
import { MultiContract11 } from '@app/rest-client/faxe/models/multi-contract-11';
import { SessionService } from '@app/services/session/session.service';
import { SessionKeys } from '@app/constants/session-keys';
import { BehaviorSubject, bufferTime, concatMap, Observable, of, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { UserToken } from '@app/services/password/model/user-token';
import { defined, groupBy, isNotEmpty, pushToMap } from '@app/fremework/functions';
import { SelectedRaceService } from '@app/services/selected-race.service';
import { SliceState } from '@app/store/faxe/sapiens-entity-state';
import { BasicEvent } from '@app/facade-services/client-event/model/basic-event';
import { UniqueIdService } from '@app/facade-services/unique-id-service';
import { MultiContractData } from '@app/services/signal-r/model/multicontract-data';
import {  addMultiContractDataBatch, purgeMultiContractDataByIds
} from '@app/store/faxe/multi-contract/multi-contract.actions';
import { selectMultiContractDataSateAll } from '@app/store/faxe/multi-contract/multi-contract.selector';
import { identifier } from '@app/store/faxe/multi-contract/multi-contract-reducer';
import { SignalRConnectionFactoryService } from '@app/services/signal-r/signal-r-connection-factory.service';


@Injectable({
  providedIn: 'root'
})
export class SignalRService implements OnDestroy {
  private readonly baseUrl: string = AppConfigService.environmentConfigData.faxeChatHub;
  private connection: HubConnection;
  private versionMap: Map<string, number> = new Map<string, number>();
  private cmdPurge = 'Purge';
  private userToken: UserToken;
  private unsubscribe$ = new Subject<void>();

  private basicEventSubject = new BehaviorSubject<BasicEvent>(null);
  public basicEvent$: Observable<BasicEvent> = this.basicEventSubject.asObservable();
  private timers = new Map<string, any>();

  private incoming$ = new Subject<MultiContractData>();
  private readonly bufferMs = 800;
  private readonly maxBufferSize = 12;

  constructor(private readonly store: Store,
              private readonly ngZone: NgZone,
              private readonly passwordService: PasswordService,
              private readonly sessionService: SessionService,
              private readonly selectedRaceService: SelectedRaceService,
              private readonly uniqueIdService: UniqueIdService,
              private readonly signalRConnectionFactoryService: SignalRConnectionFactoryService) {
  }

  options: IHttpConnectionOptions = {
    accessTokenFactory: () => {
    return this.userToken.token;
    },
  };

  private setupBuffer(): void {
    this.incoming$
      .pipe(
        bufferTime(this.bufferMs, undefined, this.maxBufferSize),
        filter(batch => !!batch && batch.length > 0),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(batch => this.flushBatch(batch));
  }

  private flushBatch(batch: MultiContractData[]): void {
    if (!batch || batch.length === 0) return;

    this.store.dispatch(addMultiContractDataBatch({
      multiContractDataBatch: batch,
      issuerStack: ['SignalRService']
    }));
  }

  init() {
    this.log('Init SignalR Service', null, true);

    this.setupBuffer();

    this.passwordService.raceUserToken()
      .pipe(
        filter(tokens => !!tokens),
        takeUntil(this.unsubscribe$))
      .subscribe((userToken) => {
        if(!this.userToken){
          this.initSignalR(userToken);
        } else {
          this.userToken = userToken;
        }
      });

    this.SubscribeForStoreIncomingData();
  }

  stop(issuer: string) {
    if (this.connection) {
      this.connection.stop().then(() => {
        this.connection = null;
        this.log('SignalR connection stopped', issuer, true);
      });
    }
  }

  SubscribeForStoreIncomingData() {

    this.store.select(selectMultiContractDataSateAll())
      .pipe(
        takeUntil(this.unsubscribe$),
        concatMap((multiContractDataList) => {

          const grouped = groupBy(
            multiContractDataList, (m) => m.multiContract.dataType
          );

          const processedIdentifiers: string[] = [];

          grouped.forEach((queue, type) => {
            let processedVersion = this.versionMap.get(type) ?? (queue[0]?.multiContract.version - 1);
            queue.sort((a, b) => a.multiContract.version - b.multiContract.version);

            queue = queue.filter(md => md.multiContract.version > processedVersion);

            const messageOperationBuffer = new Map<string, MultiContractData[]>();
            while (isNotEmpty(queue) && queue[0].multiContract.version === processedVersion + 1) {
              this.clearTimer(type);
              const nextMultiContractData = queue.shift();
              processedIdentifiers.push(identifier(nextMultiContractData));

              pushToMap(messageOperationBuffer, nextMultiContractData.multiContract.operation,  nextMultiContractData);

              this.versionMap.set(type, nextMultiContractData.multiContract.version);
              processedVersion = nextMultiContractData.multiContract.version;
            }

            messageOperationBuffer
              .forEach((md, operation) =>
                this.processMessage(operation, type, md ));

            if (isNotEmpty(queue) && queue[0].multiContract.version !== processedVersion) {
              this.startTimer(type, processedVersion + 1);
            }
          });

          // Dispatch purge action with IDs after processing
          if (isNotEmpty(processedIdentifiers)) {
            this.store.dispatch(purgeMultiContractDataByIds({ ids: processedIdentifiers }));
          }

          return of(null);
        })
      )
      .subscribe();
  }

  private startTimer(type: string, expectedVersion: number) {
    if (this.timers.has(type)) return;
    this.log(`Start timer for type ${type}, expected ${expectedVersion}`);
    this.timers.set(type, setTimeout(() => {
      this.onMissingSequence(type, expectedVersion);
    }, 2000));
  }

  private onMissingSequence(type: string, expectedVersion: number) {
    this.onSignalVersionDiff(type, expectedVersion);
  }

  private clearTimer(type: string) {
    if (this.timers.has(type)) {
      clearTimeout(this.timers.get(type));
      this.timers.delete(type);
    }
  }

  log(msg: string, data?: unknown, consoleLog = false) {
    const basicEvent: BasicEvent = {
      uniqueId: this.uniqueIdService.generateUUID(),
      eventType: 'SignalR',
      msg,
      timestamp: new Date().toLocaleTimeString(),
      data
    };
    if (consoleLog) {
      if(data) {
        console.log(msg, data);
      } else {
        console.log(msg);
      }

    }

    this.basicEventSubject.next(basicEvent);
  }

  initSignalR(userToken: UserToken) {

    if (!defined(userToken)) {
      this.log('No user tokens for SignalR! Exit...', null, true);
      return;
    }

    if (defined(this?.connection?.state)) {
      this.log(`SignalR already has connection. Exit...`, this.connection.state, true);
      return;
    }

    this.log('Init SignalR: ', userToken, true);

    this.userToken = userToken;

    this.log('Init signal R with url: ' + this.baseUrl);
    this.connection = this.signalRConnectionFactoryService.createConnection(
      this.baseUrl,
      this.options,
      (msg, data) => this.log(msg, data)
    );

    this.connection.onreconnected((connectionId) => {
      this.log('Signal R reconnected with connectionid ' + connectionId);

      this.ngZone.run(() => {
        this.connectionReastablished(connectionId, this.connection?.state);

        this.versionMap.forEach((value, key) => {
          this.log(`Fetching from server cache  ${key} version ${value + 1}`, this.versionMap, true);
          this.store.dispatch(onSignalVersionDiff(
            {
              multiMessageEnum: key,
              currentVersion: value + 1,
              raceGuid: this.sessionService.get(SessionKeys.selectedRaceId)
            }));
        });
      });
    });

    this.connection.onreconnecting((error) => {
      this.log('Signal R error: ' + error);
      this.ngZone.run(() => {
        this.connectionReConnection(error?.name, error?.message, error?.stack, this.connection.state);
      });
    });

    this.connection.on('ReceivedMessage', (user, msg) => {
      const multicontract: MultiContract11 = JSON.parse(msg);
      const data = JSON.parse(multicontract?.data);
      if (multicontract?.dataType && data) {
        this.handleMessageInNgZone(multicontract, data);
      }
    });

    this.connection.start();

    this.ngZone.run(() => {
      this.store.dispatch(
        onSignalRStatus(
          { model: { status: '-', action: 'start' } }));
    });
  }

  handleMessageInNgZone(multicontract: MultiContract11, data: any) {
    this.ngZone.run(() =>
      this.putToQueue(multicontract, data));
  }

  handleMultiMessageDataString(multiContract11s: MultiContract11[]) {
    multiContract11s.forEach(multiContract => {
      const data = JSON.parse(multiContract?.data);
      this.putToQueue(multiContract, data);
    });
  }

  putToQueue(multiContract: MultiContract11, data: any) {
    if (multiContract.raceGuid !== this.selectedRaceService.selectedRaceGuid) {
      console.log('wrong race expected ', this.selectedRaceService.selectedRaceGuid, multiContract.raceGuid);
      return;
    }

    const newVersion = this.validateVersion(multiContract.dataType, multiContract.version);

    this.log(`received message not yet in store. ${multiContract.dataType}, operation ${multiContract.operation},
       version ${multiContract.version} (${newVersion === 0 ? 'OK' : (newVersion - multiContract.version)})`, data
    );

    this.incoming$.next({ multiContract, data });
  }

  onSignalVersionDiff(dataType: null | string, expectedVersion: number) {
    this.store.dispatch(
      onSignalRStatus(
        { model: { status: 'connected', action: 'message received', errorMessage: 'version diff' } }));
    this.store.dispatch(onSignalVersionDiff(
      {
        multiMessageEnum: dataType,
        currentVersion: expectedVersion,
        raceGuid: this.sessionService.get(SessionKeys.selectedRaceId)
      }));
    return;
  }

  processMessage(operation: string, dataType: string, md: MultiContractData[]) {
    this.store.dispatch(
      onSignalRStatus(
        { model: { status: 'connected', action: 'message received' } }));

    switch (dataType) {
      case MultiMessageEnum.riderEvent:
        this.handleRiderEvent(operation, md.map(d => d.data).filter(d => defined(d)));
        break;
      case MultiMessageEnum.rider:
        this.handleRider(operation, md.map(d => d.data).filter(d => defined(d)));
        break;
      case MultiMessageEnum.resultCode:
        break;
      case MultiMessageEnum.raceClass:
        break;
      case MultiMessageEnum.race:
        break;
      case MultiMessageEnum.fenceResult:
        this.handleFenceResult(operation, md.map(d => d.data).filter(d => defined(d)));
        break;
      case MultiMessageEnum.fence:
        break;
      case MultiMessageEnum.cmd:
        md.forEach(m => {
          this.handleCmd(m.data);
        })

        break;
    }
  }

  handleRiderEvent(operation: any, riderEvent: RiderEvent[]) {
    switch (operation) {
      case MultiMessageOperationEnum.add:
        this.patchManyRiderEvent(riderEvent);
        break;
      case MultiMessageOperationEnum.update:
        this.notImplemented(operation, 'FenceResult');
        break;
      case MultiMessageOperationEnum.delete:
        this.notImplemented(operation, 'FenceResult');
        break;
    }
  }

  handleRider(operation: any, rider: Rider[]) {
    switch (operation) {
      case MultiMessageOperationEnum.add:
        this.patchManyRiders(rider);
        break;
      case MultiMessageOperationEnum.update:
        this.updateManyRiders(rider);
        break;
      case MultiMessageOperationEnum.delete:
        this.notImplemented(operation, 'FenceResult');
        break;
    }
  }

  handleFenceResult(operation: any, fenceResult: FenceResult[]) {
    switch (operation) {
      case MultiMessageOperationEnum.add:
        this.patchManyFenceResult(fenceResult);
        break;
      case MultiMessageOperationEnum.update:
        this.notImplemented(operation, 'FenceResult');
        break;
      case MultiMessageOperationEnum.delete:
        this.notImplemented(operation, 'FenceResult');
        break;
      default: {
        this.log('FenceResult Not implemented', operation);
      }
    }
  }

  handleCmd(data: any) {
    if (data?.Command === this.cmdPurge) {
      this.purgeStore('SignalRService');
    }
  }

  validateVersion = (dataType: string, newVersion: number): number => {
    if (!this.versionMap.has(dataType)) {
      this.versionMap.set(dataType, newVersion - 1);
    }
    const processedVersion = this.versionMap.get(dataType);
    return processedVersion + 1 === newVersion ? 0 : processedVersion;
  };

  patchManyFenceResult = (fenceResult: FenceResult[]): void => {
    this.store.dispatch(
      patchManyFenceResult(
        {
          models: fenceResult.map(d => ({
            ...d
          })),
          issuerStack: ['SignalRService']
        }));
  };

  purgeStore = (correlationId: string): void => {
    this.log('PurgeStore!');
    this.store.dispatch(
      purgeRider(
        {
          issuerStack: ['SignalRService'],
          correlationId
        }));

    this.store.dispatch(
      purgeFenceResult(
        {
          issuerStack: ['SignalRService'],
          correlationId
        }));

    this.store.dispatch(
      purgeRiderEvents(
        {
          issuerStack: ['SignalRService'],
          correlationId
        }));

    this.store.dispatch(
      fetchAllRider(
        {
          raceGuid: this.selectedRaceService.selectedRaceGuid,
          requiredSliceState: SliceState.total,
          issuerStack: ['SignalRService']
        }));
  };

  patchManyRiders = (riders: Rider[]): void => {
    this.store.dispatch(
      patchManyRider(
        {
          models: riders,
          issuerStack: ['SignalRService']
        }));
  };

  updateManyRiders = (riders: Rider[]): void => {
    this.store.dispatch(
      updateManyRider(
        {
          models: riders,
          issuerStack: ['SignalRService']
        }));
  };

  patchManyRiderEvent = (riderEvents: RiderEvent[]): void => {
    this.store.dispatch(
      patchManyRiderEvents(
        {
          models: riderEvents.map(d => ({
            ...d
          })), issuerStack: ['SignalRService']
        }));
  };

  connectionReastablished(connectionId: string, status: string) {
    this.store.dispatch(
      onSignalRStatus(
        { model: { connectionId, status, action: 'connectionReastablished' } }));
  }

  connectionReConnection(errorName: string, errorMessage: string, errorStack: string, status: string) {
    this.store.dispatch(
      onSignalRStatus(
        { model: { errorName, errorMessage, errorStack, status, action: 'reconnecting' } }));
  }

  notImplemented(operation: any, dataType: string) {
    this.log('Not implemented ' + operation, dataType);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
